import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { type ICellRendererParams } from "ag-grid-community";
import { Select } from "shared/components/forms/Select";
import { InputSize } from "shared/components/forms/Input";
import { getErrorMessage } from "utils/error";
import addressApi, { QUERY_GET_PROPERTIES } from "api/landlord/address";
import { Property } from "types/landlord/address";
import { PermissionTooltip } from "shared/app/PermissionTooltip";
import { useEditDetailsPermissions } from "shared/hooks/use-edit-details-permissions";

type TemplateOption = { label: string; value: string };

type TemplateEditCellVariant = "screening" | "listing";

interface MutationVars {
  propertyId: number;
  identifier: string;
}

interface Props extends ICellRendererParams<Property> {
  variant: TemplateEditCellVariant;
  options: TemplateOption[];
  queryKeyExtras?: unknown[];
}

export const TemplateEditCell = ({
  data,
  variant,
  options,
  queryKeyExtras = []
}: Props) => {
  const queryClient = useQueryClient();
  const { canEditPropertyDetails, propertyEditDeniedTooltip } =
    useEditDetailsPermissions();
  const property = data;

  const currentIdentifier =
    (variant === "screening"
      ? property?.screening_template?.identifier
      : property?.listing_template?.identifier) ?? "";

  const effectiveName =
    variant === "screening"
      ? property?.screening_template?.name
      : property?.listing_template?.name;

  const usesGroupTemplate =
    variant === "screening" &&
    property?.use_property_group_verification_template === true;

  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const localValue = pendingValue ?? currentIdentifier;

  const { mutate, isPending } = useMutation<unknown, Error, MutationVars>({
    mutationKey: ["PROPERTIES", property?.id, "UPDATE"],
    mutationFn: ({ propertyId, identifier }) => {
      if (variant === "screening")
        return addressApi.editProperty({
          propertyId,
          verification_template_id: identifier
        });
      return addressApi.editProperty({
        propertyId,
        prescreening_template_identifier: identifier
      });
    },
    onSuccess: async () => {
      const label =
        variant === "screening" ? "Screening template" : "Listing template";
      toast.success(`${label} updated`);
      await queryClient.invalidateQueries({
        queryKey: [QUERY_GET_PROPERTIES, ...queryKeyExtras]
      });
      setPendingValue(null);
    },
    onError: (error) => {
      setPendingValue(null);
      const label =
        variant === "screening" ? "Screening template" : "Listing template";
      toast.error(`Failed to update ${label}: ${getErrorMessage(error)}`);
    }
  });

  if (!property) return null;

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next = e.target.value;
    if (next === currentIdentifier) return;

    setPendingValue(next);
    mutate({ propertyId: property.id, identifier: next });
  };

  return (
    <div
      className="flex items-center h-full w-full"
      onClick={(e) => e.stopPropagation()}
    >
      <PermissionTooltip tooltip={propertyEditDeniedTooltip}>
        <Select
          id={`template-edit-${variant}-${property.id}`}
          value={localValue}
          placeholder={
            usesGroupTemplate ? "Property group" : effectiveName || "Default"
          }
          onChange={handleChange}
          isDisabled={isPending || usesGroupTemplate || !canEditPropertyDetails}
          options={options}
          className="w-full"
          size={InputSize.SM}
          legacyErrorMargins={false}
          searchable
          menuPortalTarget={document.body}
          menuPosition="fixed"
          menuPlacement="auto"
        />
      </PermissionTooltip>
    </div>
  );
};
